import './ContactsApp.scss';
import './renderers';
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { AddressBook, MagnifyingGlass, Plus, UserPlus, CheckCircle, UsersThree, Star } from '@phosphor-icons/react';
import { usePageTitle } from '../../hooks/useDocumentTitle';
import useSpaceStore from '../../stores/spaceStore';
import { useContactsAppStore } from './store/contactsAppStore';
import { ContactList } from './components/ContactList/ContactList';
import { ContactDetail } from './components/ContactDetail/ContactDetail';
import { ContactForm } from './components/ContactForm/ContactForm';
import { ContactsEmailWall } from './components/ContactsEmailWall/ContactsEmailWall';
import { ContactsView } from './types/state';
import { TIER_LABELS } from './types/entity';

export interface ContactsAppProps {
  windowId: string;
  spaceId: string;
  onClose?: () => void;
}

const VIEWS: { id: ContactsView; label: string; icon: React.ReactNode }[] = [
  { id: 'all',   label: 'All Contacts',    icon: <UsersThree size={15} weight="duotone" /> },
  { id: 'close', label: TIER_LABELS.close, icon: <Star size={15} weight="duotone" /> },
];

export const ContactsApp: React.FC<ContactsAppProps> = ({ windowId, spaceId, onClose }) => {
  const updateWindow = useSpaceStore(state => state.updateWindow);
  usePageTitle('Contacts', undefined, 'contacts');

  const store = useContactsAppStore(windowId, spaceId);

  const isInitialized = store((s) => s.isInitialized);
  const isLoading = store((s) => s.isLoading);
  const error = store((s) => s.error);
  const gmailConnected = store((s) => s.gmailConnected);
  const currentView = store((s) => s.currentView);
  const selectedContactId = store((s) => s.selectedContactId);
  const isFormOpen = store((s) => s.isFormOpen);
  const editingContactId = store((s) => s.editingContactId);
  const isDiscovering = store((s) => s.isDiscovering);
  const isImporting = store((s) => s.isImporting);
  const importResult = store((s) => s.importResult);
  const discoverResult = store((s) => s.discoverResult);
  const filter = store((s) => s.filter);

  const initialize = store((s) => s.initialize);
  const cleanup = store((s) => s.cleanup);
  const setView = store((s) => s.setView);
  const setFilter = store((s) => s.setFilter);
  const openForm = store((s) => s.openForm);
  const closeForm = store((s) => s.closeForm);
  const discoverContacts = store((s) => s.discoverContacts);
  const importFromCsv = store((s) => s.importFromCsv);

  useEffect(() => {
    updateWindow(windowId, { title: 'Contacts' });
    if (!isInitialized) {
      initialize(windowId, spaceId);
    }
    return () => { cleanup(); };
  }, [windowId, spaceId]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleDiscover = useCallback(async () => {
    await discoverContacts();
  }, [discoverContacts]);

  const handleImportClick = useCallback(() => {
    setImportError(null);
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportError(null);
    try {
      await importFromCsv(file, file.name.toLowerCase().includes('linkedin') ? 'linkedin' : 'csv');
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed');
    }
  }, [importFromCsv]);

  if (isLoading && !isInitialized) {
    return (
      <div className="contacts-app contacts-app--loading">
        <div className="contacts-app__spinner" />
        <p>Loading contacts...</p>
      </div>
    );
  }

  if (error && !isInitialized) {
    return (
      <div className="contacts-app contacts-app--error">
        <AddressBook size={32} weight="duotone" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="contacts-app">
      {gmailConnected === false && (
        <ContactsEmailWall onConnected={() => initialize(windowId, spaceId)} />
      )}
      {/* Sidebar */}
      <aside className="contacts-app__sidebar">
        <div className="contacts-app__sidebar-header">
          <AddressBook size={20} weight="duotone" />
          <span>Contacts</span>
        </div>

        <nav className="contacts-app__nav">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              className={`contacts-app__nav-item${currentView === v.id ? ' contacts-app__nav-item--active' : ''}`}
              onClick={() => setView(v.id)}
            >
              {v.icon}
              {v.label}
            </button>
          ))}
        </nav>

        <div className="contacts-app__sidebar-actions">
          <button
            className="contacts-app__action-btn contacts-app__action-btn--primary"
            onClick={() => openForm()}
          >
            <Plus size={14} weight="bold" />
            New Contact
          </button>
          <button
            className={`contacts-app__action-btn${isDiscovering ? ' contacts-app__action-btn--loading' : ''}`}
            onClick={handleDiscover}
            disabled={isDiscovering}
            title="Auto-discover contacts from Gmail and Calendar"
          >
            <UserPlus size={14} weight="bold" />
            {isDiscovering ? 'Discovering…' : 'Discover'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {discoverResult && !isDiscovering && (
            <div className="contacts-app__import-result">
              <CheckCircle size={12} weight="fill" />
              {discoverResult.discovered > 0
                ? `${discoverResult.discovered} new contact${discoverResult.discovered !== 1 ? 's' : ''} found`
                : 'No new contacts found'}
            </div>
          )}
          {importResult && !isImporting && (
            <div className="contacts-app__import-result">
              <CheckCircle size={12} weight="fill" />
              {[
                importResult.created > 0 ? `${importResult.created} added` : null,
                importResult.updated > 0 ? `${importResult.updated} updated` : null,
                importResult.skipped > 0 ? `${importResult.skipped} skipped` : null,
              ].filter(Boolean).join(' · ')}
            </div>
          )}
          {importError && (
            <div className="contacts-app__import-error">{importError}</div>
          )}
        </div>
      </aside>

      {/* Contact list */}
      <div className="contacts-app__list-panel">
        <div className="contacts-app__search">
          <MagnifyingGlass size={14} className="contacts-app__search-icon" />
          <input
            type="text"
            placeholder="Search contacts…"
            value={filter.search}
            onChange={(e) => setFilter({ search: e.target.value })}
            className="contacts-app__search-input"
          />
        </div>
        <ContactList windowId={windowId} spaceId={spaceId} />
      </div>

      {/* Detail panel */}
      <div className="contacts-app__detail-panel">
        {selectedContactId ? (
          <ContactDetail windowId={windowId} spaceId={spaceId} />
        ) : (
          <div className="contacts-app__empty-detail">
            <AddressBook size={40} weight="duotone" />
            <p>Select a contact to view details</p>
          </div>
        )}
      </div>

      {isFormOpen && (
        <ContactForm
          windowId={windowId}
          spaceId={spaceId}
          contactId={editingContactId ?? undefined}
          onClose={closeForm}
        />
      )}
    </div>
  );
};
